/**
 * Route-contextual guided-tour launcher (EPIC #1952 Wave 1, issue #1953).
 *
 * A header icon button -- mounted in BOTH shells' top bars (AdminLayout +
 * CrmLayout, which the back-office /paralegal /finance /management /accounts /
 * finance-accounts surfaces all reuse) beside the search / notifications /
 * feedback controls. Tapping it starts the guided tour registered for the route
 * currently in focus, via the EXISTING tour engine (``useTour().startTour``) --
 * exactly the launch path the Training ``GuidedToursPanel`` and the first-login
 * ``WelcomePrompt`` use, so it records ``TourProgress`` identically and is
 * relaunchable (clicking again replays).
 *
 * HIDDEN-when-no-tour (UX decision): if the current route has no registered
 * tour OR the resolved tour is not available for this persona/modules
 * (``isTourAvailable`` is false), the launcher renders nothing. A persistently
 * disabled control on every uncovered page reads as broken-promise clutter for a
 * non-technical realtor; a button that is simply ABSENT until a page actually
 * has a tour is cleaner and never offers a dead end. Coverage grows page by page
 * in #1955/#1956 by extending ``routeTours.ts`` -- the launcher then appears on
 * those routes automatically.
 *
 * Does NOT touch the welcome prompt or the Training relaunch path: it only calls
 * the shared ``startTour`` (idempotent restart), so it cannot interfere with the
 * first-login eligibility flow or the panel's own controls.
 */
import { GraduationCap } from "lucide-react";
import { useTour } from "../../contexts/tour";
import { useIsViewingAs } from "../../contexts/viewAs";
import { useRouteTourId } from "../../lib/tours/useTourGate";

export function TourLauncher() {
  const { startTour } = useTour();

  // Resolve the route to a tour the engine would actually offer this persona
  // (audience + step gating, and the #2688 opening-route check). Either miss =>
  // no launcher on this route. Shared with the help assistant's "Take a tour".
  const tourId = useRouteTourId();
  // A tour records progress with a write the server refuses while viewing as
  // an agent (D2-SEC-011, #3962), so there is no launcher then.
  const viewingAs = useIsViewingAs();

  if (!tourId || viewingAs) return null;

  return (
    <button
      type="button"
      data-tour="header-tour-launcher"
      onClick={() => startTour(tourId)}
      className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg hover:bg-[var(--color-bg-page)] transition-colors"
      aria-label="Take a tour of this page"
      title="Take a tour of this page"
    >
      <GraduationCap className="h-5 w-5 text-[var(--color-text-secondary)]" />
    </button>
  );
}
